const Device = require('@/lib/Devices/Device');
const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('./Logging/logFactory');
const { container } = require('@/container');

/**
 * Container for the current device
 * When this is initialized it loads any existing device from the cache and is then updated later.
 *
 * @constructor
 *
 * @param $rootScope
 * @param {AgentService} agentService
 * @param {Hydrator} hydrator
 * @param {LocalStorageDataStore} localStorageDataStore
 * @param {SyncStateManager} syncStateManager
 */
const CurrentDevice = function (
    $rootScope,
    agentService,
    hydrator,
    localStorageDataStore,
    syncStateManager
) {
    this.$rootScope = $rootScope;
    this.agentService = agentService;
    this.hydrator = hydrator;
    this.localStorageDataStore = localStorageDataStore;
    this.syncStateManager = syncStateManager;

    /**
     * @type {ConsoleLogger}
     */
    this.log = logFactory('CurrentDevice');

    this.localStorageKey = 'device';

    /**
     * @type {!Device}
     */
    this.device = new Device();
};

CurrentDevice.prototype = {
    initialize() {
        let cachedDevice = this.loadCachedDevice();
        if (cachedDevice) {
            this.setDevice(cachedDevice, true);
        }
    },

    /**
     * @returns {Device}
     */
    getDevice() {
        return this.isSet() ? this.device : null;
    },

    getDeviceGuid() {
        return this.device ? this.device.guid : null;
    },

    getDeviceId() {
        return this.device ? this.device.ID : null;
    },

    getTradingSessionID() {
        return (this.device ? this.device.currentTradingSessionID : null) || null;
    },

    isSet() {
        return !!(this.device && this.device.ID > 0);
    },

    /**
     * Set the current device.
     *
     * @param {Device} device
     * @param {boolean} [fromCache]
     */
    setDevice(device, fromCache) {
        this.device = device.ID ? device : new Device();
        this.cache();
        this.saveDeviceGuidToDisk(device.guid).catch(() => {});

        if (this.isSet() && !fromCache) {
            // Store a "last synced" date for the current device.
            // This is used to make it so the current user is only refreshed when you click the Sync button and not
            // every time you open ProPoint.
            this.syncStateManager.saveLastSync('currentDevice', null);
        }

        if (this.isSet()) {
            this.$rootScope.$broadcast(EventNames.DEVICE_SET, {
                device: this.device,
                fromCache,
            });
        }
    },

    clearDevice() {
        this.setDevice(new Device());
    },

    /**
     * @param {TradingSession} tradingSession
     */
    setTradingSession(tradingSession) {
        if (this.isSet()) {
            this.device.currentTradingSessionID = tradingSession.ID;
            this.cache();
        }
    },

    /**
     * @return {Device|null}
     * @private
     */
    loadCachedDevice() {
        let cachedDeviceFromLocalStorage = this.loadDeviceFromLocalStorage();
        if (cachedDeviceFromLocalStorage) {
            this.log.log('Loaded current device from local storage.');
            return cachedDeviceFromLocalStorage;
        }
        this.log.log('No current device found in local storage.');

        return null;
    },

    /**
     * @return {Device|null}
     * @private
     */
    loadDeviceFromLocalStorage() {
        let cachedDevice = this.localStorageDataStore.find(this.localStorageKey);
        if (cachedDevice && cachedDevice.ID) {
            return this.hydrator.hydrate(cachedDevice, new Device());
        }

        return null;
    },

    /**
     * Store the current device in the cache.
     */
    cache() {
        if (this.device && this.device.ID) {
            this.localStorageDataStore.persist(this.localStorageKey, this.device);
        } else {
            this.localStorageDataStore.remove(this.localStorageKey);
        }
    },

    /**
     * @param {string} deviceGuid
     *
     * @return {Promise<never>}
     */
    saveDeviceGuidToDisk(deviceGuid) {
        if (!deviceGuid) {
            return Promise.reject(new Error('Refused to save empty guid.'));
        }

        return this.agentService.writeFileToDataDir(
            'device-guid.txt',
            deviceGuid
        ).then((result) => {
            this.log.debug('Wrote guid to disk.', deviceGuid);
            return result;
        }).catch((err) => {
            this.log.error('Failed to write guid to disk.', err);
            throw err;
        });
    },

    /**
     * @return {Promise<?string>}
     */
    readDeviceGuidFromDisk() {
        return this.agentService.readFileFromDataDir(
            'device-guid.txt'
        ).then((guid) => {
            return guid;
        }).catch((err) => {
            this.log.error('Failed to read guid from disk.', err);
            throw err;
        });
    },

    updatePrintConfigForCurrentDevice(newPrintConfig) {
        const jsonPrintConfig = JSON.stringify(newPrintConfig);
        this.device.printConfig = jsonPrintConfig;
        this.cache();

        // Use the container to prevent a circular dependency if ApiClient were a dependency of this class.
        // CurrentDevice -> ApiClient -> ApiRequestParams -> CurrentDevice
        const apiClient = container.apiClient;
        return apiClient
            .request(
                'PUT',
                `devices/${container.currentDevice.device.ID}/printconfig`,
                {
                    printConfig: jsonPrintConfig,
                }
            )
            .then(() => {
                return true;
            });
    },
};

module.exports = CurrentDevice;
